import { get, map, compact, uniqBy } from "utils/lodash";
import dayjs from "utils/day";
import helper from "utils/helper";

export type PackageShop = {
  id: string;
  name: string;
  slug?: string;
  displayedAddress?: string;
  avatar?: string;
  serviceName?: string;
  menuPrice?: number;
};

export type PackageInfo = {
  id: string;
  name: string;
  description: string;
  cover: string;
  coverAlt: string;
  price: number;
  pricePerUse: number;
  quantity: number;
  durationDays: number;
  durationMonths: number;
  gracePeriodDays: number;
  maxPerUser: number;
  serviceName: string;
  menuPrice: number; // giá lẻ 1 buổi (cao nhất trong các chi nhánh)
  originalTotal: number; // quantity * menuPrice
  savingPct: number;
  savingAmount: number;
  applyScope: string;
  chainName: string;
  shops: PackageShop[];
  startDate?: string;
  endDate?: string;
  expired: boolean;
  termsHtml: string;
  warning: string;
};

/** Chuẩn hoá data prepaidPackage từ CMS thành object dễ render */
export const toPackageInfo = (pkg: any): PackageInfo | null => {
  if (!pkg?.id) return null;

  const mappings = get(pkg, "shopServiceMappings", []) || [];
  let shops: PackageShop[] = compact(
    map(mappings, (m: any) => {
      const shop = get(m, "shop");
      if (!shop?.id) return null;
      return {
        id: shop.id,
        name: shop.name,
        slug: shop.slug,
        displayedAddress: shop.displayedAddress,
        avatar: helper.imageUrl(get(shop, "avatar.url")) || "",
        serviceName: get(m, "service.name"),
        menuPrice: Number(get(m, "service.menuPrice")) || 0,
      };
    }),
  );
  // Gói áp dụng 1 shop / 1 dịch vụ
  const singleShop = get(pkg, "singleShop");
  if (!shops.length && singleShop?.id) {
    shops = [
      {
        id: singleShop.id,
        name: singleShop.name,
        slug: singleShop.slug,
        displayedAddress: singleShop.displayedAddress,
        avatar: helper.imageUrl(get(singleShop, "avatar.url")) || "",
        serviceName: get(pkg, "singleShopService.name"),
        menuPrice: Number(get(pkg, "singleShopService.menuPrice")) || 0,
      },
    ];
  }
  shops = uniqBy(shops, "id");

  const quantity = Number(get(pkg, "quantity")) || 0;
  const price = Number(get(pkg, "price")) || 0;
  const pricePerUse =
    Number(get(pkg, "pricePerUse")) ||
    (quantity ? Math.round(price / quantity) : 0);
  const menuPrice = Math.max(
    0,
    ...map(shops, (s) => s.menuPrice || 0),
    Number(get(pkg, "singleShopService.menuPrice")) || 0,
  );
  const originalTotal = menuPrice * quantity;
  const savingAmount = originalTotal > price ? originalTotal - price : 0;
  const savingPct =
    originalTotal > price && originalTotal > 0
      ? Math.round((savingAmount / originalTotal) * 100)
      : 0;

  const media = get(pkg, "media");
  const coverRaw =
    get(media, "sizes.card.url") || get(media, "url") || "";
  const cover = coverRaw
    ? helper.imageUrl(coverRaw) || ""
    : get(shops, "0.avatar") || "";

  const endDate = get(pkg, "endDate");
  const expired = !!endDate && dayjs(endDate).isValid()
    ? dayjs(endDate).endOf("day").isBefore(dayjs())
    : false;

  const durationDays = Number(get(pkg, "durationDays")) || 0;

  return {
    id: pkg.id,
    name: get(pkg, "name") || "",
    description: get(pkg, "description") || "",
    cover,
    coverAlt: get(media, "alt") || get(pkg, "name") || "",
    price,
    pricePerUse,
    quantity,
    durationDays,
    durationMonths: durationDays ? Math.round(durationDays / 30) : 0,
    gracePeriodDays: Number(get(pkg, "gracePeriodDays")) || 0,
    maxPerUser: Number(get(pkg, "maxPerUser")) || 0,
    serviceName:
      get(shops, "0.serviceName") || get(pkg, "singleShopService.name") || "",
    menuPrice,
    originalTotal,
    savingPct,
    savingAmount,
    applyScope: get(pkg, "applyScope") || "",
    chainName: get(pkg, "shopChain.name") || "",
    shops,
    startDate: get(pkg, "startDate"),
    endDate,
    expired,
    termsHtml: get(pkg, "termsBodyHtml") || "",
    warning: get(pkg, "bookingReadinessWarning") || "",
  };
};
